"use client";

import * as React from "react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";

// -----------------------------------------------------------------------------
// Types & Context
// -----------------------------------------------------------------------------

type LinkOrSidebarContentProps = {
  to?: string;
  onClick?: React.AnchorHTMLAttributes<HTMLAnchorElement>["onClick"];
  className?: string;
  children: React.ReactNode; // for links, this is <Icon/> then <span>Label</span>
  "aria-label"?: string;
  "data-testid"?: string;
};

type Item = { key: React.Key; node: React.ReactElement<LinkOrSidebarContentProps> };

type HeaderContextValue = {
  overflowCount: number;
  sidebarOpen: boolean;
  isMobile: boolean;
  hasOverflow: boolean;
  sidebarWidth: number;
};

type HeaderContextState = HeaderContextValue & {
  setHeaderState?: React.Dispatch<React.SetStateAction<HeaderContextValue>>;
};

const HeaderContext = React.createContext<HeaderContextState>({
  overflowCount: 0,
  sidebarOpen: false,
  isMobile: false,
  hasOverflow: false,
  sidebarWidth: 256
});

export function useHeaderContext() {
  return React.useContext(HeaderContext);
}

// -----------------------------------------------------------------------------
// Root <Header>
// -----------------------------------------------------------------------------

type HeaderRootProps = React.PropsWithChildren<{
  className?: string;
  hasSidebarContent?: boolean;
  "data-testid"?: string;
}>;

export function Header({ children, className, hasSidebarContent, "data-testid": testId }: HeaderRootProps) {
  // Get sidebar state
  const { open: sidebarOpen, isMobile } = useSidebar();
  const { setHeaderState, sidebarWidth } = useHeaderContext();

  // Extract compound parts
  const left = React.Children.toArray(children).find(
    (c: any) => React.isValidElement(c) && c.type === Header.Left
  ) as React.ReactElement | undefined;

  const center = React.Children.toArray(children).find(
    (c: any) => React.isValidElement(c) && c.type === Header.Center
  ) as React.ReactElement | undefined;

  const right = React.Children.toArray(children).find(
    (c: any) => React.isValidElement(c) && c.type === Header.Right
  ) as React.ReactElement | undefined;

  // --- Refs ------------------------------------------------------------------
  const centerRef = React.useRef<HTMLDivElement>(null);
  const rightRef = React.useRef<HTMLDivElement>(null);
  const measureRef = React.useRef<HTMLUListElement>(null);
  const overflowToggleSizerRef = React.useRef<HTMLButtonElement>(null);

  // --- Center items (only consider <Header.Link/>) ----------------------------
  const centerChildren = (((center as any)?.props?.children) ?? []) as React.ReactNode;
  const centerItems: Item[] = React.Children.toArray(centerChildren)
    .filter((c: any) => React.isValidElement(c) && c.type === Header.Link)
    .map((node: any, i) => ({ key: node.key ?? i, node }));

  // --- Sidebar items (consider both links and sidebar content) -----------------
  const sidebarItems: Item[] = React.Children.toArray(centerChildren)
    .filter((c: any) => React.isValidElement(c) && (c.type === Header.Link || c.type === Header.SidebarContent))
    .map((node: any, i) => ({ key: node.key ?? i, node }));

  // --- Visible/overflow counts ----------------------------------------------
  const [showHamburger, setShowHamburger] = React.useState(hasSidebarContent ?? false);
  const overflowCount = showHamburger ? centerItems.length : 0;
  const hasOverflow = showHamburger;

  // --- Fit calculation (single, simple rig) ---------------------------------
  const EPSILON = 1; // drop as soon as we would clip by >= 1px

  const measure = React.useCallback(() => {
    // Skip measure logic if hasSidebarContent is true - always show sidebar
    if (hasSidebarContent) {
      setShowHamburger(true);
      return;
    }

    const centerEl = centerRef.current;
    const rig = measureRef.current;
    if (!centerEl || !rig) return;

    // Current available width for center (no toggle reserved yet)
    const baseAvailable = Math.max(0, centerEl.clientWidth - EPSILON);

    // Helper: check if all items fit within 'cap' using the rig
    const allItemsFit = (widthCap: number) => {
      const items = Array.from(rig.children) as HTMLElement[];
      if (!items.length) return true;
      const left = items[0].getBoundingClientRect().left;
      const lastRight = items[items.length - 1].getBoundingClientRect().right;
      const totalWidth = lastRight - left; // includes flex gap
      return totalWidth <= widthCap;
    };

    // Check if all items fit without any overflow
    const allFit = allItemsFit(baseAvailable);

    if (allFit) {
      setShowHamburger(false);
      return;
    }

    // As soon as we can't fit all items, show hamburger menu
    setShowHamburger(true);
  }, [centerItems.length, hasSidebarContent]);

  // Re-measure when center/right size changes or item count changes
  useResizeObserver(centerRef as any, () => measure());
  useResizeObserver(rightRef as any, () => measure());

  React.useLayoutEffect(() => {
    measure();
  }, [measure, centerItems.length]);

  const visibleItems = showHamburger ? [] : centerItems;
  const overflowItems = showHamburger ? sidebarItems : [];

  // Update parent context if setHeaderState is provided
  React.useEffect(() => {
    if (setHeaderState) {
      setHeaderState(prev => ({
        ...prev,
        overflowCount,
        sidebarOpen,
        isMobile,
        hasOverflow
      }));
    }
  }, [overflowCount, sidebarOpen, isMobile, hasOverflow, setHeaderState]);

  // --- Render live center strip ---------------------------------------------
  const renderCenter = (
    <ul className="flex w-full items-center gap-2 overflow-hidden">
      {visibleItems.map(({ key, node }) => (
        <li key={key} className="shrink-0">
          {node}
        </li>
      ))}
    </ul>
  );

  // --- Hidden measuring rig --------------------------------------------------
  const renderMeasuringRig = (
    <div
      aria-hidden
      className="pointer-events-none absolute -left-[9999px] -top-[9999px] isolate opacity-0"
    >
      {/* Sizer for the toggle (exact same classes) */}
      <button ref={overflowToggleSizerRef} className={OVERFLOW_TOGGLE_CLASSES}>
        <span aria-hidden>⋯</span>
      </button>

      {/* One rig that matches live link layout */}
      <ul ref={measureRef} className="flex w-[1600px] items-center gap-2">
        {centerItems.map(({ key, node }) => (
          <li key={key} className="shrink-0">
            <HeaderLinkMeasurable>{node.props.children}</HeaderLinkMeasurable>
          </li>
        ))}
      </ul>
    </div>
  );

  const handleSidebarWidthChange = React.useCallback((newWidth: number) => {
    if (setHeaderState) {
      setHeaderState(prev => ({
        ...prev,
        sidebarWidth: newWidth
      }));
    }
  }, [setHeaderState]);

  return (
    <>
      {hasOverflow && (
        <OverflowSidebar
          items={overflowItems}
          hasSidebarContent={hasSidebarContent}
          width={sidebarWidth}
          onWidthChange={handleSidebarWidthChange}
        />
      )}

      <div
className={cn(
          "z-50 flex h-14 w-full items-stretch gap-3 border-b border-sidebar-border bg-sidebar text-sidebar-foreground px-3",
          className
        )}
        role="banner"
        data-testid={testId}
      >
        <div className="flex shrink-0 items-center gap-2">
          {(left as any)?.props?.children}
        </div>

        <div ref={centerRef} className="relative min-w-0 flex-1 flex items-center">
          {renderCenter}
          {renderMeasuringRig}
        </div>

        <div ref={rightRef} className="flex shrink-0 items-center gap-2">
          {(right as any)?.props?.children}
        </div>
      </div>
    </>
  );
}

// Resize observer helper
function useResizeObserver(
  ref: React.RefObject<Element>,
  onResize: ResizeObserverCallback
) {
  React.useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(onResize);
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [ref, onResize]);
}

// Keep this in one place so our hidden sizer == real toggle
const OVERFLOW_TOGGLE_CLASSES =
  "inline-flex items-center justify-center gap-2.5 rounded-xl border border-border/30 px-2.5 py-2 text-sm transition-all duration-150 ease-in-out " +
  "hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 active:scale-[0.98]";

// -----------------------------------------------------------------------------
// Compound parts
// -----------------------------------------------------------------------------

Header.Left = function HeaderLeft({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return <div className={className}>{children}</div>;
};

Header.Center = function HeaderCenter({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return <div className={className}>{children}</div>;
};

Header.Right = function HeaderRight({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return <div className={className}>{children}</div>;
};

Header.Link = function HeaderLink({
  to,
  onClick,
  className,
  children,
  ...a11y
}: LinkOrSidebarContentProps & { to: string }) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className={cn(
        "flex items-center gap-2.5 rounded-xl px-3 py-2 whitespace-nowrap transition-all duration-150 ease-in-out",
        "hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 active:scale-[0.98]",
        className
      )}
      {...a11y}
    >
      {children}
    </Link>
  );
};

Header.SidebarContent = function HeaderSidebarContent({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) {
  return <div className={className}>{children}</div>;
};

// Hidden link used only inside the measuring rig
function HeaderLinkMeasurable({ children }: React.PropsWithChildren) {
  return (
    <a className="flex items-center gap-2.5 rounded-xl px-3 py-2 whitespace-nowrap">
      {children}
    </a>
  );
}

Header.OverflowToggle = function HeaderOverflowToggle({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  const { overflowCount } = React.useContext(HeaderContext);
  if (overflowCount <= 0) return null;

return (
    <SidebarTrigger
    className={cn(OVERFLOW_TOGGLE_CLASSES, className)}
    aria-label="Open overflow menu"
    data-testid="sidebar-trigger"
    >
        {children ?? <span aria-hidden>⋯</span>}
    </SidebarTrigger>
  );
};

// -----------------------------------------------------------------------------
// HeaderInset - Layout component that adjusts for sidebar state
// -----------------------------------------------------------------------------

export const HeaderInset = React.forwardRef<HTMLDivElement, React.ComponentProps<"main">>(
  ({ className, style, ...props }, ref) => {
    const { open, isMobile } = useSidebar();
    const { hasOverflow, sidebarWidth } = useHeaderContext();

    // Only apply margin if sidebar is open AND we have overflow items
    const shouldApplyMargin = !isMobile && open && hasOverflow;

    return (
      <main
        ref={ref}
        data-header-inset
        className={cn(
          "relative flex flex-1 flex-col bg-background transition-[margin-left] duration-200 ease-linear",
          className
        )}
        style={{
          ...(shouldApplyMargin && {
            marginLeft: `${sidebarWidth}px`
          }),
          ...style
        }}
        {...props}
      />
    );
  }
);
HeaderInset.displayName = "HeaderInset";

Header.Inset = HeaderInset;

// -----------------------------------------------------------------------------
// Overflow sidebar (shadcn)
// -----------------------------------------------------------------------------

const STORAGE_KEY = 'header-sidebar-width';
const DEFAULT_WIDTH = 256; // 16rem in pixels
const MIN_WIDTH = 200;
const MAX_WIDTH = 600;

function OverflowSidebar({
  items,
  hasSidebarContent,
  width,
  onWidthChange
}: {
  items: Item[];
  hasSidebarContent?: boolean;
  width: number;
  onWidthChange: (width: number) => void;
}) {
const location = useLocation();
  const [isResizing, setIsResizing] = React.useState(false);
  const sidebarRef = React.useRef<HTMLDivElement>(null);

  // Separate link items from sidebar content items
  const linkItems = items.filter(({ node }) => node.props.to);
  const sidebarContentItems = items.filter(({ node }) => !node.props.to);

  // Set width via DOM when not resizing (for initial load and after resize completes)
  React.useEffect(() => {
    if (hasSidebarContent && sidebarRef.current && !isResizing) {
      // Find the actual fixed sidebar element inside the wrapper
      const fixedSidebar = sidebarRef.current.querySelector('[class*="fixed"][class*="w-[--sidebar-width]"]') as HTMLElement;
      const spacerDiv = sidebarRef.current.querySelector('[class*="relative"][class*="w-[--sidebar-width]"]') as HTMLElement;

      if (fixedSidebar) {
        fixedSidebar.style.width = `${width}px`;
      }
      if (spacerDiv) {
        spacerDiv.style.width = `${width}px`;
      }
    }
  }, [hasSidebarContent, width, isResizing]);

  // Save to localStorage when width changes (only when not resizing)
  React.useEffect(() => {
    if (hasSidebarContent && !isResizing) {
      localStorage.setItem(STORAGE_KEY, width.toString());
    }
  }, [width, hasSidebarContent, isResizing]);

  const handleMouseDown = React.useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  React.useEffect(() => {
    if (!isResizing || !hasSidebarContent) return;

    // Get references to the actual sidebar elements that need resizing
    const fixedSidebar = sidebarRef.current?.querySelector('[class*="fixed"][class*="w-[--sidebar-width]"]') as HTMLElement;
    const spacerDiv = sidebarRef.current?.querySelector('[class*="relative"][class*="w-[--sidebar-width]"]') as HTMLElement;

    // Disable ALL transitions on sidebar elements
    if (fixedSidebar) {
      fixedSidebar.style.transition = 'none';
    }
    if (spacerDiv) {
      spacerDiv.style.transition = 'none';
    }

    // Disable transitions on HeaderInset elements
    const insets = document.querySelectorAll('[data-header-inset]');
    insets.forEach(el => {
      if (el instanceof HTMLElement) {
        el.style.transition = 'none';
      }
    });

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = e.clientX;
      if (newWidth >= MIN_WIDTH && newWidth <= MAX_WIDTH) {
        // Directly update DOM for smooth resizing
        if (fixedSidebar) {
          fixedSidebar.style.width = `${newWidth}px`;
        }
        if (spacerDiv) {
          spacerDiv.style.width = `${newWidth}px`;
        }

        // Update inset elements - only marginLeft, width will adjust naturally
        const insets = document.querySelectorAll('[data-header-inset]');
        insets.forEach(el => {
          if (el instanceof HTMLElement) {
            el.style.marginLeft = `${newWidth}px`;
          }
        });
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      setIsResizing(false);

      // Clamp final width to min/max
      const finalWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, e.clientX));

      // Update DOM to clamped value before re-enabling transitions
      if (fixedSidebar) {
        fixedSidebar.style.width = `${finalWidth}px`;
      }
      if (spacerDiv) {
        spacerDiv.style.width = `${finalWidth}px`;
      }
      const insets = document.querySelectorAll('[data-header-inset]');
      insets.forEach(el => {
        if (el instanceof HTMLElement) {
          el.style.marginLeft = `${finalWidth}px`;
        }
      });

      // Update CSS variable on the root container
      const rootContainer = sidebarRef.current?.closest('[style*="--sidebar-width"]') as HTMLElement;
      if (rootContainer) {
        rootContainer.style.setProperty('--sidebar-width', `${finalWidth}px`);
      }

      // Re-enable transitions on sidebar elements
      if (fixedSidebar) {
        fixedSidebar.style.transition = '';
      }
      if (spacerDiv) {
        spacerDiv.style.transition = '';
      }

      // Re-enable transitions on HeaderInset elements
      insets.forEach(el => {
        if (el instanceof HTMLElement) {
          el.style.transition = '';
        }
      });

      // Update React state
      onWidthChange(finalWidth);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, hasSidebarContent, onWidthChange]);

  if (items.length === 0) return null;

return (
    <Sidebar ref={sidebarRef}>
      <SidebarContent className="flex flex-col">
        {/* Render link items in a menu if there are any */}
        {linkItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
{linkItems.map(({ key, node }) => {
                  const to = node.props.to!;
                  const testId = node.props["data-testid"];
                  const sidebarTestId = testId ? `sidebar-${testId}` : undefined;
                  const ariaLabel = node.props["aria-label"];
                  return (
                    <SidebarMenuItem key={key}>
                      <SidebarMenuButton asChild isActive={location.pathname === to}>
                        <Link  
                          to={to}  
                          data-testid={sidebarTestId} 
                          aria-label={ariaLabel} 
                        >
                          {node.props.children}
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {/* Render sidebar content items (like custom sidebar content) directly */}
        {sidebarContentItems.map(({ key, node }) => (
          <React.Fragment key={key}>
            {node.props.children}
          </React.Fragment>
        ))}
      </SidebarContent>
      {hasSidebarContent && (
        <div
          onMouseDown={handleMouseDown}
          className={cn(
            "absolute right-0 top-0 h-full w-1 cursor-ew-resize bg-transparent hover:bg-primary/20 transition-all duration-150 ease-in-out",
            isResizing && "bg-primary/40"
          )}
          style={{ zIndex: 100 }}
        />
      )}
    </Sidebar>
  );
}

// HeaderProvider that wraps SidebarProvider and provides HeaderContext
export function HeaderProvider({ children, ...sidebarProps }: React.ComponentProps<typeof SidebarProvider>) {
  const [headerState, setHeaderState] = React.useState<HeaderContextValue>(() => {
    let initialWidth = DEFAULT_WIDTH;
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        initialWidth = parseInt(stored, 10);
      }
    }
    return {
      overflowCount: 0,
      sidebarOpen: false,
      isMobile: false,
      hasOverflow: false,
      sidebarWidth: initialWidth
    };
  });

  const containerRef = React.useRef<HTMLDivElement>(null);

  // Update CSS variable when width changes
  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.setProperty('--sidebar-width', `${headerState.sidebarWidth}px`);
    }
  }, [headerState.sidebarWidth]);

  return (
    <div ref={containerRef} style={{ '--sidebar-width': `${headerState.sidebarWidth}px` } as React.CSSProperties}>
      <SidebarProvider {...sidebarProps}>
        <HeaderContext.Provider value={{ ...headerState, setHeaderState }}>
          {children}
        </HeaderContext.Provider>
      </SidebarProvider>
    </div>
  );
}

export default Header;